import React, { useCallback, useEffect, useRef, useState } from 'react';
import TextSettings, { TextSettingsEnum } from '@picsart/rc/old/TextSettings';
import { useDispatch, useSelector } from 'react-redux';
import { getCurrentTextId } from '../../modules/currentTextId';
import { getActiveEffects, updateEffectsParams } from 'modules/effects';
import { Effect } from 'types/Effects';
import { getCurrentSceneId } from 'modules/scenes';
import { ITextSettings } from '@picsart/rc/types/containerComponents/TextSettings/types';
import { getKonvaLayer } from 'modules/canvas';
import effectToKonvaTextSettings from 'utils/konva/effectToKonvaTextSettings';
import Konva from 'konva';
import { TRANSFORMER_DEFAULT_ID } from '../artBoard/preview/konvaContainer/transformer';
import { clone } from 'ramda';
import { getTransformedText, loadFont } from 'utils/textTool';
import { getLines } from 'utils/konva';
import { addRecentFont, getRecentFonts } from 'modules/persistedSettings';
import { saveRevision } from 'modules/revisions';
import { ToolName } from 'types/AppState';

const NUMBER_REGEXP_POSITIVE_LIMITED = /^([1-9][0-9]{0,2})$/;
const POSITIVE_NUMBER_REGEXP = /^([0-9]|[1-9][0-9]|9[0-9]|100)$/;

export const TextSettingsTool: React.FunctionComponent = () => {
  const [settings, setSettings] = useState<ITextSettings | undefined>();
  const currentTextIds = useSelector(getCurrentTextId);
  const effects = useSelector(getActiveEffects);
  const sceneId = useSelector(getCurrentSceneId);
  const konvaLayer = useSelector(getKonvaLayer);
  const recentFonts = useSelector(getRecentFonts);
  const layer = useSelector(getKonvaLayer);
  const dispatch = useDispatch();

  const effectParams = useRef<Effect[]>([]);

  useEffect(() => {
    effectParams.current = clone(effects);
  }, [effects]);

  useEffect(() => {
    setSettings({
      textFontFamily: effects[0].font.fontFamily,
      textCaps: effects[0].textCaps,
      textAlign: `space-${effects[0].textAlign}`,
      textStyle: [
        { type: 'bold', active: effects[0].isBold },
        { type: 'italic', active: effects[0].isItalic },
        { type: 'underline', active: effects[0].isUnderline }
      ],
      textSpace: [
        {
          type: 'line',
          value: effects[0].line || effects[0].line === 0 ? Math.round(effects[0].line * 20) : 20
        },
        {
          type: 'letter',
          value: effects[0].letter || effects[0].letter === 0 ? Math.round(effects[0].letter) : 0
        }
      ],
      textSize: Math.round(effects?.[0].fontSize)
    });
  }, [currentTextIds, effects[0]]);

  const batchEffectParams = (obj: (effect: Effect) => Partial<Effect>) =>
    effectParams.current.map((effect: Effect) => {
      return {
        ...effect,
        ...obj(effect)
      };
    });

  const onFontSizeChange = useCallback(
    (inputtedValue: number | '', isRecentlyValue: boolean, focusedValue: null | number) => {
      if (
        !currentTextIds.length ||
        (inputtedValue && !NUMBER_REGEXP_POSITIVE_LIMITED.test(String(inputtedValue)))
      ) {
        return;
      }
      // Update settings.
      setSettings((prev: any) => ({
        ...prev,
        textSize: inputtedValue
      }));
      // On empty space, restore last accepted value.
      const value = inputtedValue || focusedValue;

      if (!isRecentlyValue || !value) {
        return;
      }

      effectParams.current = batchEffectParams(() => ({
        fontSize: value
      }));

      dispatch(updateEffectsParams(sceneId, effectParams.current));
      dispatch(saveRevision(ToolName.Text));
      effectParams.current.forEach(effect => {
        konvaLayer.findOne(`#${effect.id}`).setAttrs(effectToKonvaTextSettings(effect));
      });
      (konvaLayer.findOne(`#${TRANSFORMER_DEFAULT_ID}`) as Konva.Transformer).forceUpdate();
      konvaLayer.draw();
    },
    [dispatch, settings, currentTextIds, effects, effectParams, batchEffectParams]
  );

  const onSettingsChange = useCallback(
    async setting => {
      if (!currentTextIds.length || !konvaLayer) {
        return;
      }
      const { type, value, isRecentlyValue, changedSettingType } = setting;

      const applyFontStyle = () => {
        effectParams.current = batchEffectParams(({ isBold, isItalic, isUnderline }) => ({
          isBold: type === 'bold' ? !isBold : isBold,
          isItalic: type === 'italic' ? !isItalic : isItalic,
          isUnderline: type === 'underline' ? !isUnderline : isUnderline
        }));
        setSettings(settings => ({
          ...settings,
          textStyle: settings.textStyle.map(el =>
            el.type === type ? { ...el, active: !el.active } : el
          )
        }));
      };

      const applyFontAlign = () => {
        effectParams.current = batchEffectParams(() => ({
          textAlign: type.substr(type.indexOf('-') + 1)
        }));
        setSettings(settings => ({
          ...settings,
          textAlign: type
        }));
      };

      const applyFontSpace = () => {
        if (value && !POSITIVE_NUMBER_REGEXP.test(String(value))) {
          return;
        }

        const intValue = parseInt(value, 10);
        const newValue = !Number.isNaN(intValue)
          ? intValue
          : settings.textSpace.find(space => space.type === type).value;

        effectParams.current = batchEffectParams(effect => {
          const textArr = getLines(effect);
          return {
            textArr,
            [type]: newValue / (type === 'line' ? 20 : 1)
          };
        });

        setSettings(settings => ({
          ...settings,
          textSpace: settings.textSpace.map(el => (el.type === type ? { ...el, value } : el))
        }));
      };

      const applyFontCaps = () => {
        effectParams.current = batchEffectParams(effect => {
          const transformedText = getTransformedText(type, effect.text);
          const textCaps = type;

          const textEffect = {
            ...effect,
            transformedText,
            textCaps
          };

          return {
            ...textEffect,
            textArr: getLines(textEffect)
          };
        });
        setSettings(settings => ({
          ...settings,
          textCaps: type
        }));
      };

      const applyFont = async () => {
        const font = {
          fontFamily: value.font_family,
          src: value.font_src
        };

        await loadFont(font);

        dispatch(addRecentFont(value));

        effectParams.current = batchEffectParams(effect => {
          const textArr = getLines(effect, font.fontFamily);
          return {
            font,
            textArr,
            presetId: null
          };
        });

        setSettings(settings => ({
          ...settings,
          textFontFamily: font.fontFamily
        }));
      };

      setting.changedSettingType === TextSettingsEnum.textSpace && applyFontSpace();
      setting.changedSettingType === TextSettingsEnum.textStyle && applyFontStyle();
      setting.changedSettingType === TextSettingsEnum.textAlign && applyFontAlign();
      setting.changedSettingType === TextSettingsEnum.textCaps && applyFontCaps();
      setting.changedSettingType === TextSettingsEnum.textFontFamily && (await applyFont());
      effectParams.current.forEach(effect => {
        konvaLayer.findOne(`#${effect.id}`).setAttrs(effectToKonvaTextSettings(effect));
      });

      layer.draw();
      if (!isRecentlyValue && changedSettingType === TextSettingsEnum.textSpace) {
        return;
      }
      dispatch(updateEffectsParams(sceneId, effectParams.current));
      dispatch(saveRevision(ToolName.Text));
      effectParams.current.forEach(effect => {
        konvaLayer.findOne(`#${effect.id}`).setAttrs(effectToKonvaTextSettings(effect));
      });
      (konvaLayer.findOne(`#${TRANSFORMER_DEFAULT_ID}`) as Konva.Transformer).forceUpdate();
      konvaLayer.draw();
    },
    [currentTextIds, settings, effects, dispatch, konvaLayer, effectParams, batchEffectParams]
  );

  return (
    <>
      <TextSettings
        changedType="number"
        activeSettings={settings}
        onSettingsChange={onSettingsChange}
        onFontSizeChange={onFontSizeChange}
        recentlyUsedFonts={recentFonts}
        features={['typeFace', 'size', 'fontSettings', 'letter', 'line', 'caps', 'align']}
      />
    </>
  );
};
